import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API } from "../lib/api";

const CustomerContext = createContext();

export const CustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [skipLogin, setSkipLogin] = useState(false);

  // Initialize from localStorage
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem("repairro_customer_token");
      const savedCustomer = localStorage.getItem("repairro_customer");

      if (savedToken && savedCustomer) {
        setToken(savedToken);
        setCustomer(JSON.parse(savedCustomer));
      }
    } catch (err) {
      console.error("Failed to restore customer session:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendLoginOtp = async (phone) => {
    try {
      const response = await axios.post(`${API}/customer/auth/otp/send`, {
        phone,
      });
      return { success: true, data: response.data };
    } catch (err) {
      console.error("OTP send failed:", err);
      return {
        success: false,
        error: err.response?.data?.detail || "Failed to send OTP",
      };
    }
  };

  const loginWithOtp = async (phone, otp) => {
    try {
      const response = await axios.post(`${API}/customer/auth/otp/verify`, {
        phone,
        otp,
      });

      const { token: jwtToken, customer: customerData, is_new } = response.data;

      setToken(jwtToken);
      setCustomer(customerData);

      localStorage.setItem("repairro_customer_token", jwtToken);
      localStorage.setItem("repairro_customer", JSON.stringify(customerData));

      setShowLoginModal(false);

      return { success: true, is_new, customer: customerData };
    } catch (err) {
      console.error("OTP verification failed:", err);
      return {
        success: false,
        error: err.response?.data?.detail || "Login failed",
      };
    }
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem("repairro_customer_token");
    localStorage.removeItem("repairro_customer");
  };

  const updateProfile = async (updates) => {
    try {
      const response = await axios.patch(`${API}/customer/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCustomer(response.data);
      localStorage.setItem("repairro_customer", JSON.stringify(response.data));

      return { success: true, customer: response.data };
    } catch (err) {
      console.error("Failed to update profile:", err);
      return {
        success: false,
        error: err.response?.data?.detail || "Update failed",
      };
    }
  };

  const requestLogin = () => {
    setShowLoginModal(true);
  };

  const hideLogin = () => {
    setShowLoginModal(false);
    setSkipLogin(true);
  };

  const resetSkip = () => {
    setSkipLogin(false);
  };

  const value = {
    customer,
    token,
    loading,
    showLoginModal,
    skipLogin,
    sendLoginOtp,
    loginWithOtp,
    logout,
    updateProfile,
    requestLogin,
    hideLogin,
    resetSkip,
    isAuthenticated: !!customer && !!token,
  };

  return (
    <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error("useCustomer must be used within CustomerProvider");
  }
  return context;
};
